// AgentReviewPanel.jsx — agent 预读的呈现**与作答**（SB-078 L2/L3，第六十六批）。
//
// 呈现次序与折叠态的**理由**写在 pages/agentReview.mjs 的头注里（那是需求方本批的
// 裁决，不是排版偏好）：**问题在前，建议默认折叠**。
//
// ── L3 加的那一半：这个面现在能作答 ────────────────────────────────────────
// 一次提交做完三件事（服务端在同一个事务里）：记裁决台账、推进工单、关掉其余审批人
// 的通知。**答完即转态**是需求方本批的裁决——反面那条的代价很具体：答案进了台账、
// 工单还挂着 pending，两个事实并存且都不假。
//
// 已被别人裁过的题**不再显示表单**，显示结论与裁决人。先到先得之后，给第二个人一个
// 看起来还能答的空表单，是在请他做一件已经没有了的事。
//
// ── 第一百一十批 B：三态可分辨、逃生口、人工再 grill、工作项草案一键采纳 ─────
// 本批之前，这个面板在 `agentReview` 为 null 时**整块不渲染**，于是「没跑 / 跑失败了 /
// 该类型不做预读」三件事逐像素相同——三者的下一步动作却完全不同（等 / 重跑 / 什么都
// 不用做）。现在它们各有各的话，失败态还带得出原因摘要（数据一直在 job 表里，缺的
// 是投影与分辨），且旁边就是重跑与"再 grill 一轮"两个人工入口。
//
// 呈现次序也是判据：**状态 → 逃生口 → 重跑按钮**紧挨着排。理由是它们合成一个闭环
// ——agent 说它缺什么，审批人在重跑的自由补充里把那句话补上，再触发一轮。分在两块
// 的话，那句"我缺 X"就只是一句无处可去的抱怨。
import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { errKey } from '../api/errText.mjs';
import { useLang, useT } from '../i18n/index.mjs';
import {
  normalizeReview, normalizeRulings, hasSomethingToShow, emptyAnswers,
  missingAnswers, canSubmit, toggleOption, toPayload, rulingFor,
  i18nQuestionMap, displayQuestion,
  adoptChecked, directiveLabelKey, draftAdoptState, ruleByCode,
  askDraftState, askDraftText,
  normalizeHistory, normalizeStatus, statusSeverity, statusTextKey, statusTextVars,
} from '../pages/agentReview.mjs';
import AgentRerunDialog from './AgentRerunDialog.jsx';
import { fmtTime } from './TicketDetailView.jsx';

const ACTIONS = ['accept', 'reject', 'request_info'];

export default function AgentReviewPanel({
  raw, rulings: rawRulings, no, onRule, submitLang,
  status: rawStatus, history: rawHistory, onRerun,
}) {
  const t = useT();
  const { lang } = useLang();
  const review = normalizeReview(raw);
  const rulings = normalizeRulings(rawRulings);
  // 永不为 null（旧后端 ⇒ state:''，呈现为"什么都不说"，且 canRetry:false）。
  const status = normalizeStatus(rawStatus);
  const history = normalizeHistory(rawHistory);

  const [openRec, setOpenRec] = useState(false);
  const [openHistory, setOpenHistory] = useState(false);
  const [rerunOpen, setRerunOpen] = useState(false);
  const [queuedRound, setQueuedRound] = useState(0);
  const [answers, setAnswers] = useState(() => emptyAnswers(review));
  const [action, setAction] = useState('');
  const [adopt, setAdopt] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [errAdopt, setErrAdopt] = useState(false);
  // 受理派生出的工作项编号。**必须显出来**：一个"点了受理，然后什么都没说"的界面，
  // 人不会知道系统替他建了东西——而不知道就等于没建（他还会再手工建一条）。
  // 第一百一十批 D：连同"这条到底是不是按草案建的"一起显（服务端回 draftAdopted /
  // draftSkippedReason）——**采纳与否必须有收据**，否则采纳这个动作与不采纳同形。
  const [derived, setDerived] = useState(null);
  const [derivedNote, setDerivedNote] = useState('');
  // ── 第一百一十一批 D（OQ-C50）：按 agent 的问题发 request_info ────────────
  // `usedAskDraft` 记的是「这段批注是从 agent 的问题填进来的」。它**不改变发出去的
  // 文本**（发出去的永远是 note，即他编辑之后那一版），只决定服务端要不要把 agent
  // 的原文逐字留一份痕——「agent 问了什么」与「人最终问了什么」是两件事，而前者是
  // 评估这个 agent 有没有用的唯一数据。
  const [usedAskDraft, setUsedAskDraft] = useState(false);
  const [askConfirm, setAskConfirm] = useState(false);
  const [askFilled, setAskFilled] = useState(0);
  const [askSent, setAskSent] = useState(0);

  const statusKey = statusTextKey(status);
  const escalation = status.escalation;
  const draft = review?.workItemDraft ?? null;
  const rerunnable = Boolean(onRerun && no && status.canRetry);

  // 面板本身可以没有预读（agent 没跑），但只要有话说就该显示：能作答、有已裁的题、
  // 有状态要交代、有一份逃生口报告——任何一条成立都比一片空白强。
  const answerable = Boolean(onRule && no);
  if (!hasSomethingToShow(review) && rulings.length === 0 && !answerable
      && !statusKey && !escalation) return null;

  const recLabel = (r) => (t(`agentReview.rec.${r}`) === `agentReview.rec.${r}`
    ? r : t(`agentReview.rec.${r}`));
  const questions = review?.questions ?? [];
  const open = questions.filter((q) => !rulingFor(rulings, q.questionKey));
  const missing = missingAnswers({ questions: open }, answers);
  // 第九十九批（SB-085）：界面语言 ≠ 提交语言时取译文映射（无译文 = null，
  // 原样显示 + 语言标注）。映射从 raw 取——normalizeReview 刻意只收已知字段。
  const qMap = submitLang && submitLang !== lang ? i18nQuestionMap(raw, lang) : null;

  const setAnswer = (key, next) => setAnswers((a) => ({ ...a, [key]: next }));

  // 采纳勾选框的三态判据（有没有草案 / 已经关联过 / 动作不是受理）在 .mjs 里。
  // `existingNo` 只有在服务端回过 already_linked 之后才知道——工单详情不投影"这张单
  // 已经关联了哪条工作项"，所以采纳前无法预判，采纳后必须说清（见 derivedNote）。
  const adoptState = draftAdoptState({ draft, action, existingNo: '' });

  // 「按 agent 的问题请求补充」那个按钮的三态（判据在 .mjs，这里只渲染它的输出）。
  // `noteDirty` = 批注里已经有字 ⇒ 点下去先确认一次，**绝不静默合并**：合并出来的
  // 那段话没有作者。
  const askState = askDraftState({
    askSubmitter: review?.askSubmitter, action, noteDirty: note.trim() !== '',
  });

  // 真的填进去。三件事一起做，因为它们是同一个动作的三面：切动作、填草案、记来源。
  const fillAskDraft = () => {
    const text = askDraftText(review?.askSubmitter, t);
    if (!text) return;   // 空清单 ⇒ 什么都不做（按钮本就不显示，这是第二道）
    setAction('request_info');
    setNote(text);
    setUsedAskDraft(true);
    setAskFilled(review?.askSubmitter?.length ?? 0);
    setAskConfirm(false);
  };

  const submit = async () => {
    setBusy(true);
    setErr(null);
    // 记下**这一次**发没发 adoptDraft：服务端把两条草案错误都落成 RULE_VIOLATION
    // （只有英文 message 不同），而分辨它们靠的是我们自己知道的这个事实，不是猜
    // message。存在 state 里而不是渲染时现算——报错之后用户可能改动作，那时
    // 现算出来的答案说的是另一次提交。
    const sentAdopt = adoptChecked(adopt, action);
    setErrAdopt(sentAdopt);
    try {
      // ⚠ `review` 而不是 `{questions: open}`：`toPayload` 的答案侧只看 questions
      // （已裁过的题不再回传），而 askKey 要从**服务端投影的那个字段**取。传裁剪过的
      // 对象进去，askKey 恒为空串 ⇒ 每一次按草案发 request_info 都吃 409 STALE_ASK。
      const r = await onRule(toPayload({ questions: open, askKey: review?.askKey ?? '' },
                                       answers, action, note,
                                       { adoptDraft: sentAdopt, usedAskDraft }));
      if (r?.derivedNo) setDerived(r.derivedNo);
      // 收据：这一次到底是不是照 agent 的问题发的、发了几条。服务端回的**它自己
      // 记下的那个数**，不是前端现算的——两者不一致时该信服务端。
      setAskSent(r?.infoRequestFromAsk ? (Number(r?.askCount) || 0) : 0);
      // 三种收据各说各的：按草案建的 / 这张单早已关联（草案被忽略）/ 老办法建的。
      // 第三种沿用既有那句 `agentReview.derived`，所以这里只在前两种上加话。
      if (r?.draftAdopted) setDerivedNote('adopted');
      else if (r?.draftSkippedReason === 'already_linked') setDerivedNote('already_linked');
      else setDerivedNote('');
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderColor: 'primary.light' }}>
      {/* 以下几处 flexWrap+useFlexGap（第九十一批）：360px 视口下 Chip 会被省略号
          截断——语言提示、「推荐」标记恰恰在最需要它们的长文本旁边消失。掉行保全文。 */}
      <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
        <Typography sx={{ fontWeight: 700 }} variant="subtitle2">
          {t('agentReview.title')}
        </Typography>
        {review ? <Chip size="small" variant="outlined" label={t('agentReview.draftChip')} /> : null}
        {/* 第六十九批裁「标注原语言 + 立单待触发」；第九十九批触发翻转为**双向**
            （SB-085）：EN 工单的题面正被中文审批人生读。有译文时题面按界面语言
            显示、标「机器翻译·未校」（OQ-B27 徐呈口径，原文仍是正本——作答与
            台账永远落原文）；译文未到/失败时保留原样标注，不隐瞒事实。 */}
        {Boolean(submitLang) && submitLang !== lang ? (
          qMap ? (
            <Chip size="small" variant="outlined" color="default"
                  label={t('locale.machine')} />
          ) : (
            <Chip size="small" variant="outlined" color="default"
                  label={t('agentReview.langNote', { lang: submitLang })} />
          )
        ) : null}
      </Stack>

      {/* ── ⓪ 预读的状态：三态（实为八态）各有各的话 ────────────────────────
          本批之前这一块整个不存在，于是"没跑 / 跑失败了 / 该类型不做预读"在屏幕上
          逐像素相同。三条纪律都在这里落地：
            · 判据在服务端（agentReviewStatus.state），前端只做 state → 键的映射；
            · **失败原因分类的空串读作「原因不明」而不是「可以重试」**——存量作业
              行没有分类，把它说成"多半是网络抖动"就是三次徒劳重试的邀请；
            · `done` 与旧后端（state:''）不出这一块：意见本身就在下面，
              而对一个说不出状态的后端编一句状态是最坏的一种"有用"。
          重试与"再 grill 一轮"是**同一个入口**（同一只端点、同一个对话框）：
          区别只在审批人要不要给它加命令，而那件事在对话框里选。分成两个按钮会让人
          去分辨一个不存在的差别。 */}
      {statusKey || escalation ? (
        <Alert severity={statusSeverity(status)} variant="outlined" sx={{ mb: 1.5 }}>
          {/* 这一块要**自己说得出自己是什么**。面板顶上那句 `agentReview.title`
              说的是"这里是 AI 预读"，而这一行说的是"那次预读怎么样了"——两件事。
              少了这个标签，一句孤零零的"从未读过这张工单"读起来像一条报错。 */}
          <Typography variant="caption" color="text.secondary" component="div">
            {t('agentReview.status.title')}
          </Typography>
          {statusKey ? (
            <Typography variant="body2">{t(statusKey, statusTextVars(status))}</Typography>
          ) : null}
          {status.lastAtMs > 0 ? (
            <Typography variant="caption" color="text.secondary" component="div">
              {t('agentReview.status.lastAt', { when: fmtTime(status.lastAtMs, lang) })}
            </Typography>
          ) : null}

          {/* ── ② 逃生口：agent 自己给的理由 ─────────────────────────────────
              比一片空白有用得多，也是这个闭环的起点。**纯文本渲染，绝不 markdown**
              ——它可能复述 agent 读到的仓内容（第三方材料）。 */}
          {escalation ? (
            <Box sx={{ mt: 1 }}>
              <Stack direction="row" spacing={1} useFlexGap
                     sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {t('agentReview.blocked.title')}
                </Typography>
                {escalation.reason ? (
                  <Chip size="small" variant="outlined" color="warning"
                        label={t(`agentReview.blocked.reason.${escalation.reason}`)} />
                ) : null}
                {escalation.round > 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    {t('agentReview.round', { n: escalation.round })}
                  </Typography>
                ) : null}
              </Stack>
              {escalation.detail ? (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                  {t('agentReview.blocked.detail')} {escalation.detail}
                </Typography>
              ) : null}
              {escalation.wouldNeed.length > 0 ? (
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {t('agentReview.blocked.wouldNeed')}
                  </Typography>
                  <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2.5 }}>
                    {escalation.wouldNeed.map((s, i) => (
                      <Typography key={i} component="li" variant="body2">{s}</Typography>
                    ))}
                  </Stack>
                </Box>
              ) : null}
            </Box>
          ) : null}

          {/* 人工入口。`canRetry` 是**服务端的判断**（它同时看工单终局性、类型是否
              预读、在途作业、当日次数）——前端不重算，少一个条件就是一个按下去吃
              409 的按钮，而 409 解释不了"这张单已经结案了"。 */}
          {rerunnable ? (
            <Box sx={{ mt: 1 }}>
              <Button size="small" variant="outlined" onClick={() => setRerunOpen(true)}>
                {t('agentReview.rerun.open')}
              </Button>
            </Box>
          ) : null}
        </Alert>
      ) : null}

      {/* done 态下不出状态块，但重跑入口仍要在——"这份意见我不满意，再读一轮"
          与"它失败了，重跑"是同一个需求的两面，而前者才是常态。 */}
      {rerunnable && !statusKey && !escalation ? (
        <Box sx={{ mb: 1.5 }}>
          <Button size="small" variant="text" onClick={() => setRerunOpen(true)}>
            {t('agentReview.rerun.open')}
          </Button>
        </Box>
      ) : null}

      {/* 排进去了要有收据。新一轮跑完之前这一页显示的仍是**上一轮**的内容——
          不说这句的话，"我明明点了重跑，页面一点没变"就是下一条工单。 */}
      {queuedRound > 0 ? (
        <Alert severity="success" sx={{ mb: 1.5 }}>
          {t('agentReview.rerun.queued', { round: queuedRound })}
        </Alert>
      ) : null}

      {rerunnable ? (
        <AgentRerunDialog
          open={rerunOpen}
          onClose={() => setRerunOpen(false)}
          onSubmit={async (body) => {
            const r = await onRerun(body);
            setQueuedRound(Number(r?.round) || 0);
            return r;
          }}
        />
      ) : null}

      {review?.summary ? (
        <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: 'pre-wrap' }}>
          {review.summary}
        </Typography>
      ) : null}

      {/* 这一轮是**带着什么命令**跑出来的（第一百一十批 E）。审批人看到一份意见时
          必须知道它是不是被人下过令的：一份被要求"出工作项草案"的意见里出现草案，
          与一份自发出草案的意见，可信度不是一回事。 */}
      {review?.directives?.length ? (
        <Stack direction="row" spacing={0.5} useFlexGap
               sx={{ flexWrap: 'wrap', mb: 1.5 }}>
          {review.directives.map((k) => (
            <Chip key={k} size="small" variant="outlined" label={t(directiveLabelKey(k))} />
          ))}
        </Stack>
      ) : null}

      {/* 第九十三批（SB-103）：预读查阅了什么——工具循环留痕。审批人有权知道这份
          结论引用了哪些仓文件、有没有取失败的；这也是仓内容注入面的"看得见"防线：
          agent 读过的每一处都在这里，没列出的它就没读过。 */}
      {Array.isArray(review?.toolTrace) && review.toolTrace.length > 0 ? (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
          {t('agentReview.toolTrace', { n: review.toolTrace.length })}
          {review.toolTrace.map((c, i) => (
            <span key={i} style={{ display: 'block', paddingLeft: 12 }}>
              {c.ok ? '✓' : '✗'} {c.note || c.tool}
            </span>
          ))}
        </Typography>
      ) : null}

      {/* ① 已经裁过的题——结论与裁决人，不再是表单（先到先得）。 */}
      {rulings.length > 0 ? (
        <Stack spacing={1} sx={{ mb: 1.5 }}>
          {rulings.map((r, i) => (
            <Box key={i} sx={{ borderLeft: 3, borderColor: r.stale ? 'warning.main' : 'success.main', pl: 1.5 }}>
              <Typography sx={{ fontWeight: 600 }} variant="body2">{r.question}</Typography>
              <Typography variant="body2">
                {r.labels.join(' · ') || '—'}{r.other ? ` · ${r.other}` : ''}
              </Typography>
              <Typography sx={{ display: 'block' }} variant="caption" color="text.secondary">
                {t('agentReview.ruledBy', { who: r.ruledBy || '—' })} · {fmtTime(r.ruledAtMs, lang)}
                {r.round > 1 ? ` · ${t('agentReview.round', { n: r.round })}` : ''}
              </Typography>
              {/* 过期的裁决**仍然显示**——它不是没发生过，而是"当时这么裁的，后来
                  地基变了"，而那句话本身就是要给人看的。 */}
              {r.stale ? (
                <Typography sx={{ display: 'block' }} variant="caption" color="warning.main">
                  ⚠ {t('agentReview.stale', { why: r.staleReason || '—' })}
                </Typography>
              ) : null}
            </Box>
          ))}
        </Stack>
      ) : null}

      {/* ② 还没裁的题——可作答。 */}
      {open.length > 0 ? (
        <Stack spacing={1.5} sx={{ mb: 1.5 }}>
          {open.map((q) => {
            const a = answers[q.questionKey] ?? { labels: [], other: '' };
            // 显示层走译文（缺哪个字段回落原文）；**交互与回传走原文**——
            // toggleOption 收 q.options[oi].label（原文），台账因此只见原文。
            const dq = displayQuestion(q, qMap);
            return (
              <Box key={q.questionKey}>
                <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'baseline', flexWrap: 'wrap', mb: 0.5 }}>
                  {dq.header ? <Chip size="small" label={dq.header} /> : null}
                  <Typography sx={{ fontWeight: 600 }} variant="body2">{dq.question}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {q.multiSelect ? t('agentReview.multiSelect') : t('agentReview.singleSelect')}
                  </Typography>
                </Stack>
                <Stack spacing={0.5} sx={{ pl: 1.5 }}>
                  {q.options.map((o, oi) => {
                    const picked = a.labels.includes(o.label);
                    return (
                      <Box
                        key={oi}
                        onClick={answerable && !busy
                          ? () => setAnswer(q.questionKey, toggleOption(a, o.label, q.multiSelect))
                          : undefined}
                        sx={{
                          cursor: answerable && !busy ? 'pointer' : 'default',
                          borderRadius: 1, px: 1, py: 0.5,
                          border: 1,
                          borderColor: picked ? 'primary.main' : 'transparent',
                          bgcolor: picked ? 'action.selected' : 'transparent',
                        }}
                      >
                        <Stack sx={{ alignItems: 'center', flexWrap: 'wrap' }} direction="row" spacing={0.75} useFlexGap>
                          <Typography sx={{ fontWeight: o.recommended ? 600 : 400 }} variant="body2">
                            {dq.options[oi].label}
                          </Typography>
                          {o.recommended ? (
                            <Chip size="small" color="primary" variant="outlined"
                                  label={t('agentReview.recommended')} />
                          ) : null}
                        </Stack>
                        {/* 优劣分析。没有它，选项就只是两个名字。 */}
                        {dq.options[oi].description ? (
                          <Typography sx={{ display: 'block' }} variant="caption" color="text.secondary">
                            {dq.options[oi].description}
                          </Typography>
                        ) : null}
                      </Box>
                    );
                  })}
                  {q.allowOther && answerable ? (
                    <TextField
                      size="small"
                      fullWidth
                      placeholder={t('agentReview.otherPlaceholder')}
                      value={a.other}
                      disabled={busy}
                      onChange={(e) => setAnswer(q.questionKey, { ...a, other: e.target.value })}
                    />
                  ) : null}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      ) : null}

      {/* ③ 给提交人的事实性追问——受众不同，单独一块，且**明说是给谁的**。 */}
      {review?.askSubmitter?.length ? (
        <Box sx={{ mb: 1.5 }}>
          <Typography sx={{ display: 'block' }} variant="caption" color="text.secondary" gutterBottom>
            {t('agentReview.askSubmitter')}
          </Typography>
          <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2.5 }}>
            {review.askSubmitter.map((q, i) => (
              <Typography key={i} component="li" variant="body2">{q}</Typography>
            ))}
          </Stack>

          {/* ── 第一百一十一批 D（OQ-C50）：「按 agent 的问题请求补充」──────────
              设计文档 `ai-review-surface` §2 声明「给提交人的问题汇成一次
              request_info 草案」，而第一百一十批坐实**这一条从未实现**——于是审批人
              每次都把 agent 已经写好的问题手工重打一遍。这就是缺的那一下。

              按钮**紧贴问题本身**，不塞进下面的动作区：审批人是读完这几条才决定要
              不要发的，把它放到三个动作按钮旁边，等于要求他先决定再回来读。

              判据（显示态、草案正文、身份键）全在 pages/agentReview.mjs——`.jsx` 里
              不放判据（本仓为此付过账）。 */}
          {answerable && askState.show ? (
            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                disabled={busy}
                onClick={() => (askState.reason === 'willOverwrite'
                  ? setAskConfirm(true)
                  : fillAskDraft())}
              >
                {t('agentReview.ask.useDraft')}
              </Button>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                {askState.reason === 'needsRequestInfo'
                  ? t('agentReview.ask.needsRequestInfo')
                  : t('agentReview.ask.hint')}
              </Typography>
              {/* 填进去了要有收据——否则「点了按钮，下面那个框里多了一段字」这件事，
                  在一屏放不下的工单上人根本看不见。 */}
              {askFilled > 0 ? (
                <Typography variant="caption" color="success.main" component="div">
                  {t('agentReview.ask.filled', { n: askFilled })}
                </Typography>
              ) : null}
            </Box>
          ) : null}
        </Box>
      ) : null}

      {/* 覆盖确认。**绝不静默合并**：合并出来的那段话没有作者，而它会原样落进
          `ticket_feedback`，是提交人唯一看得到的东西。 */}
      <Dialog open={askConfirm} onClose={() => setAskConfirm(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('agentReview.ask.overwriteTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('agentReview.ask.overwriteBody', { n: note.length })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setAskConfirm(false)}>
            {t('agentReview.ask.cancel')}
          </Button>
          <Button size="small" variant="contained" onClick={fillAskDraft}>
            {t('agentReview.ask.overwriteOk')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ④ 建议与理由——**默认折叠**（锚定效应；见 agentReview.mjs 头注）。 */}
      {review?.recommendation ? (
        <Box sx={{ mb: 1.5 }}>
          <Button size="small" onClick={() => setOpenRec((v) => !v)}>
            {openRec ? t('agentReview.hideRec') : t('agentReview.showRec')}
          </Button>
          <Collapse in={openRec} timeout="auto" unmountOnExit>
            <Box sx={{ pt: 1 }}>
              <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
                <Typography sx={{ fontWeight: 600 }} variant="body2">
                  {recLabel(review.recommendation)}
                </Typography>
                {review.model ? (
                  <Typography variant="caption" color="text.secondary">{review.model}</Typography>
                ) : null}
              </Stack>
              {review.reasoning ? (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {review.reasoning}
                </Typography>
              ) : null}
            </Box>
          </Collapse>
        </Box>
      ) : null}

      {/* ── ④.5 工作项草案：agent 提议，人按一下（第一百一十批 D，需求方裁）──
          草案渲染成**可读的一段**而不是一坨 JSON：审批人要能一眼判断这份标题与范围
          对不对，因为它会成为计费台账的锚点。
          「采纳前要说清会发生什么」由 `draft.adoptHint` 承担：建一条工作项、关联到
          这张单、归属记裁决人（**不是** agent——工作项是台账锚点，submitter 必须是一个
          找得到、能被问责的真账号；草案是建议，按下按钮的是人）。 */}
      {draft ? (
        <Box sx={{ mb: 1.5, borderLeft: 3, borderColor: 'info.main', pl: 1.5 }}>
          <Stack direction="row" spacing={1} useFlexGap
                 sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {t('agentReview.draft.title')}
            </Typography>
            <Chip size="small" variant="outlined"
                  label={t('agentReview.draft.kind',
                           { kind: t(`agentReview.draft.kind.${draft.kind}`) })} />
          </Stack>
          <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>{draft.title}</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.25 }}>
            {t('agentReview.draft.scope')} {draft.scope}
          </Typography>
          {draft.why ? (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.25 }}>
              {t('agentReview.draft.why')} {draft.why}
            </Typography>
          ) : null}
          {draft.stakeholders.length > 0 ? (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
              {t('agentReview.draft.stakeholders')} {draft.stakeholders.join(' · ')}
            </Typography>
          ) : null}

          {/* 勾选框只在能作答时出现（读面上一个勾不了的勾是噪音）。
              不可勾时**自己说得出为什么**：一个灰着且不解释的勾选框，与"这张单不能
              采纳草案"在屏幕上完全同形，而人只会以为页面坏了。 */}
          {answerable && adoptState.show ? (
            <Box sx={{ mt: 0.5 }}>
              {/* ⚠ 勾选框**先于动作**就可勾（checked 直接跟着 adopt 走），而不是
                  "非受理时锁死"。锁死那种写法看着更安全，实际更糟：审批人多半是
                  先读草案、决定要它，最后才选动作——一个在他读草案时灰着的勾选框，
                  他会当成"这份草案不能采纳"而放弃，而那正是需求方裁的那"一下"。
                  勾了却选了不受理，**不静默丢弃**：下面那句话当场说出来
                  （工作项只在受理时建）。判据仍只有一处——toPayload 只在 accept 上
                  发 adoptDraft，与这句提示读的是同一个 adoptState.enabled。 */}
              <FormControlLabel
                control={(
                  <Checkbox
                    size="small"
                    checked={adopt}
                    disabled={busy || adoptState.reason === 'blocked'}
                    onChange={(e) => setAdopt(e.target.checked)}
                  />
                )}
                label={<Typography variant="body2">{t('agentReview.draft.adopt')}</Typography>}
              />
              <Typography
                variant="caption"
                component="div"
                color={adopt && !adoptState.enabled ? 'warning.main' : 'text.secondary'}
              >
                {adopt && !adoptState.enabled
                  ? t('agentReview.err.draftNeedsAccept')
                  : t('agentReview.draft.adoptHint')}
              </Typography>
            </Box>
          ) : null}
        </Box>
      ) : null}

      {/* ⑤ 动作 —— 答完即转态（需求方本批裁）。 */}
      {answerable ? (
        <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1.5 }}>
          {err ? (
            <Alert severity="error" sx={{ mb: 1 }}>
              {/* 六种错误各有各的处置，所以各说各的——一句"提交失败"会让人反复点
                  同一个按钮。表在 pages/agentReview.mjs（RULE_ERR_KEYS）：散在这里
                  的 byCode 字面量改一处漏一处，第九十九批为此付过账。 */}
              {t(errKey(err, { byCode: ruleByCode(errAdopt),
                              fallback: 'agentReview.err.generic' }))}
            </Alert>
          ) : null}
          {/* 第一百一十一批 D：按 agent 的问题发出去的收据。服务端回的那个数，
              不是前端现算的——两者不一致时该信服务端。 */}
          {askSent > 0 ? (
            <Alert severity="success" sx={{ mb: 1 }}>
              {t('agentReview.ask.sent', { n: askSent })}
            </Alert>
          ) : null}
          {/* SB-078 L4：受理派生出的工作项。不显出来的话人会再手工建一条。
              第一百一十批 D：**采纳与否要有收据**。少了这一句，"按草案建的"与
              "按老办法搬运既有文本建的"在屏幕上同形，而两者的标题正文完全不同。 */}
          {derived ? (
            <Alert severity="success" sx={{ mb: 1 }}>
              {derivedNote === 'adopted'
                ? t('agentReview.draft.adopted', { no: derived })
                : derivedNote === 'already_linked'
                  ? t('agentReview.draft.blockedByExisting', { no: derived })
                  : t('agentReview.derived', { no: derived })}
            </Alert>
          ) : null}
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 1 }}>
            {ACTIONS.map((k) => (
              <Button
                key={k}
                size="small"
                variant={action === k ? 'contained' : 'outlined'}
                disabled={busy}
                onClick={() => setAction(action === k ? '' : k)}
              >
                {t(`agentReview.action.${k}`)}
              </Button>
            ))}
          </Stack>
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            placeholder={t('agentReview.notePlaceholder')}
            value={note}
            disabled={busy}
            onChange={(e) => {
              setNote(e.target.value);
              // 手改过之后**不再声称是照 agent 的问题发的**？——不，仍然是：他改的
              // 正是那份草案。这一位记的是"这段话从哪儿来"，而来源不因编辑而改变
              //（发出去的文本另有 feedback 那一份权威）。清空才算放弃这个来源：
              // 一段被删光重写的话，与 agent 的那几条问题已经没有关系了。
              if (e.target.value.trim() === '') { setUsedAskDraft(false); setAskFilled(0); }
            }}
            sx={{ mb: 1 }}
          />
          {/* ── 发出去之后会发生什么：**转到哪个状态、提交人收到什么** ──────────
              第一百一十一批 D 明确要求这一句。它只在动作是「请求补充」时出现——
              在受理/不受理上说这段话是在描述另一条路。 */}
          {action === 'request_info' ? (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
              {t('agentReview.ask.willHappen')}
            </Typography>
          ) : null}
          <Stack sx={{ alignItems: 'center' }} direction="row" spacing={1.5}>
            <Button
              variant="contained"
              size="small"
              disabled={busy || !canSubmit({ questions: open }, answers, action)}
              onClick={submit}
            >
              {busy ? t('agentReview.submitting') : t('agentReview.submit')}
            </Button>
            {/* 一个不解释自己为何不可点的按钮，人只会以为页面坏了。 */}
            {missing.length > 0 ? (
              <Typography variant="caption" color="text.secondary">
                {t('agentReview.missing', { n: missing.length })}
              </Typography>
            ) : !action ? (
              <Typography variant="caption" color="text.secondary">
                {t('agentReview.pickAction')}
              </Typography>
            ) : null}
          </Stack>
        </Box>
      ) : null}

      {/* ── ⑥ 上一轮的预读（第一百一十批 C）─────────────────────────────────
          **默认折叠**，且展开后每一轮都明标"已被覆盖：只能看，不能答"。
          「一份过期的预读比没有更糟」讲的是默认呈现，不是不许留存：agent 上一轮
          建议过什么、人没有采纳，是衡量建议准确率的原料——而那件事到今天一次都没
          被验证过（ai-review-surface §3）。
          ⚠ 历史题**没有 questionKey**（服务端刻意不投影，归一化也不产出），
          所以这里连一个能提交的表单都拼不出来——「只能看」是机器保证，不是样式。 */}
      {history.length > 0 ? (
        <Box sx={{ borderTop: 1, borderColor: 'divider', mt: 1.5, pt: 1 }}>
          <Button size="small" onClick={() => setOpenHistory((v) => !v)}>
            {openHistory ? t('agentReview.history.hide')
                         : t('agentReview.history.show', { n: history.length })}
          </Button>
          <Collapse in={openHistory} timeout="auto" unmountOnExit>
            <Stack spacing={1.5} sx={{ pt: 1 }}>
              {history.map((h, i) => (
                <Box key={i} sx={{ borderLeft: 3, borderColor: 'divider', pl: 1.5, opacity: 0.85 }}>
                  <Stack direction="row" spacing={1} useFlexGap
                         sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {t('agentReview.history.round', { n: h.round })}
                    </Typography>
                    <Chip size="small" variant="outlined" label={t('agentReview.history.readOnly')} />
                    {h.supersededAtMs > 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        {t('agentReview.history.supersededAt',
                           { when: fmtTime(h.supersededAtMs, lang) })}
                      </Typography>
                    ) : null}
                  </Stack>
                  {h.directives.length > 0 ? (
                    <Typography variant="caption" color="text.secondary" component="div">
                      {t('agentReview.history.directives', {
                        list: h.directives.map((k) => t(directiveLabelKey(k))).join(' · '),
                      })}
                    </Typography>
                  ) : null}
                  {h.summary ? (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                      {h.summary}
                    </Typography>
                  ) : null}
                  {/* 题面只列题干：历史里选项的用处是"当时给了哪几个口子"，
                      而那已经由裁决台账（上面 ① 那一块）逐条记着了。 */}
                  {h.questions.length > 0 ? (
                    <Stack component="ul" spacing={0.25} sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                      {h.questions.map((q, qi) => (
                        <Typography key={qi} component="li" variant="body2">{q.question}</Typography>
                      ))}
                    </Stack>
                  ) : null}
                  {/* 那一轮建议过的工作项草案——**没有采纳按钮**（那一轮已经过去了），
                      但"它当时提过什么"正是准确率的原料。没提过就明说没提过：
                      空白会被读成"这个字段坏了"。 */}
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                    {h.workItemDraft ? h.workItemDraft.title : t('agentReview.history.noDraft')}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Collapse>
        </Box>
      ) : null}
    </Paper>
  );
}
